import {
  SOCIAL_APP_KEYS_AUDIT_ACTION,
  SOCIAL_CREDENTIAL_SET_INFO,
  isSocialCredentialSet,
  type SiteAuditEntry,
  type SocialCredentialSet,
} from '@mg/shared';

/**
 * Social Media Integrations (Site Integrations): what the card and the "Recent site changes" log use
 * beside the component itself. Kept out of the page file, which the react-refresh lint rule limits to
 * exporting components.
 */

/**
 * A saved secret as the card shows it: always these 12 bullets (U+2022), whatever the secret's real
 * length. The browser is only ever told that a secret is saved, never the secret, its length or any
 * part of it, so the bullets can say nothing more. Text in the summary strip, and the secret field's
 * placeholder; never an input's value.
 */
export const SECRET_MASK = '•'.repeat(12);

/**
 * The ID and secret inputs. Not `Input`: it pins `placeholder:text-ink-faint` (3.64:1), and `cn` only
 * joins classes, so another placeholder colour passed to it would sit beside that one instead of
 * replacing it. `ink-muted` keeps the placeholder above 4.5:1.
 */
export const KEY_INPUT_CLASS =
  'focus-ring block w-full rounded-lg border border-line-input bg-card px-3 py-2 text-sm text-ink placeholder:text-ink-muted';

/** The read-only redirect URL: monospace, as wide as its row allows beside the Copy button. */
export const URL_INPUT_CLASS =
  'focus-ring block min-w-0 flex-1 rounded-lg border border-line-input bg-card px-2 py-1.5 font-mono text-xs text-ink-strong';

/** A Replace that would change nothing: the form says this instead of sending an empty save. */
export function nothingToReplace(set: SocialCredentialSet): string {
  const info = SOCIAL_CREDENTIAL_SET_INFO[set];
  return `Enter a new ${info.idLabel} or ${info.secretLabel}, or select Cancel.`;
}

/**
 * The "Recent site changes" line for a change of one set's app keys, or null for any other entry.
 * The server's diff holds markers only ('set', 'changed', 'kept', 'cleared'). A Replace names the
 * value(s) it changed from those markers, and no diff value is ever printed.
 */
export function describeSocialAppAudit(entry: Pick<SiteAuditEntry, 'action' | 'entityId' | 'diff'>): string | null {
  const set = entry.entityId;
  if (!isSocialCredentialSet(set)) return null;
  const info = SOCIAL_CREDENTIAL_SET_INFO[set];
  switch (entry.action) {
    case SOCIAL_APP_KEYS_AUDIT_ACTION.added:
      return `Added ${info.rowName} keys`;
    case SOCIAL_APP_KEYS_AUDIT_ACTION.replaced: {
      const changed = [
        entry.diff.clientId === 'changed' ? info.idLabel : null,
        entry.diff.clientSecret === 'changed' ? info.secretLabel : null,
      ].filter((label): label is string => label !== null);
      return changed.length > 0
        ? `Replaced ${info.rowName} keys — ${changed.join(' and ')}`
        : `Replaced ${info.rowName} keys`;
    }
    case SOCIAL_APP_KEYS_AUDIT_ACTION.removed:
      return `Removed ${info.rowName} keys`;
    default:
      return null;
  }
}
